import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Landmark, Plus, Search, Upload, Download, Loader2, Sparkles, Star, Trash2, Pencil, MapPin, Mail, Globe } from 'lucide-react';
import { InvestorFormDialog } from '@/components/investors/InvestorFormDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const listify = (v) => Array.isArray(v) ? v.filter(Boolean) : String(v || '').replace(/^\[|\]$/g, '').split(',').map(s => s.replace(/^['"]|['"]$/g, '').trim()).filter(Boolean);

export default function InvestorsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [stageFilter, setStageFilter] = useState('');
  const [highlightedOnly, setHighlightedOnly] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [researching, setResearching] = useState({});
  const [bulkJob, setBulkJob] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/investors?limit=5000`, { withCredentials: true });
      setItems(data.items || []);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not load investors'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!bulkJob || bulkJob.status === 'completed' || bulkJob.status === 'failed') return;
    const t = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/investors/research/status`, { withCredentials: true });
        setBulkJob(data);
        if (data.status === 'completed' || data.status === 'failed') { clearInterval(t); load(); }
      } catch { clearInterval(t); }
    }, 3000);
    return () => clearInterval(t);
  }, [bulkJob]);

  const types = useMemo(() => [...new Set(items.map(i => (i.investor_type || '').trim()).filter(Boolean))].sort(), [items]);
  const stages = useMemo(() => [...new Set(items.flatMap(i => listify(i.investment_stage)).map(s => s.trim()).filter(s => s && !/^all stages?$/i.test(s)))].sort(), [items]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter(i => {
      if (highlightedOnly && !i.highlighted) return false;
      if (typeFilter && (i.investor_type || '') !== typeFilter) return false;
      if (stageFilter && !listify(i.investment_stage).includes(stageFilter)) return false;
      if (!q) return true;
      const hay = [i.name, i.firm_name, i.email, i.location, i.investor_type, i.description, ...listify(i.preferred_therapeutic_areas), ...listify(i.investment_focus)].join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [items, search, typeFilter, stageFilter, highlightedOnly]);

  const toggleHighlight = async (inv) => {
    try {
      const { data } = await axios.patch(`${API}/investors/${inv.id}/highlight`, {}, { withCredentials: true });
      setItems(prev => prev.map(i => i.id === inv.id ? { ...i, highlighted: data?.highlighted ?? !i.highlighted } : i));
    } catch { toast.error('Could not update highlight'); }
  };

  const research = async (inv) => {
    setResearching(p => ({ ...p, [inv.id]: true }));
    try {
      const { data } = await axios.post(`${API}/investors/${inv.id}/research`, {}, { withCredentials: true });
      if (data?.investor) setItems(prev => prev.map(i => i.id === inv.id ? data.investor : i));
      toast.success(data?.status === 'enriched' ? `Enriched ${inv.firm_name || inv.name} (${(data.fields_updated || []).length} fields)` : 'No new information found');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Research failed'); }
    finally { setResearching(p => ({ ...p, [inv.id]: false })); }
  };

  const researchAll = async () => {
    try {
      const { data } = await axios.post(`${API}/investors/research/bulk`, {}, { withCredentials: true });
      if (data.status === 'already_running') toast.info('Bulk research already running');
      else toast.success(`Researching ${data.total_firms} firms in the background`);
      setBulkJob({ status: 'running', ...data });
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not start research'); }
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append('file', file);
    try {
      const { data } = await axios.post(`${API}/investors/bulk-upload`, fd, { withCredentials: true, timeout: 180000 });
      toast.success(`Imported ${data.imported ?? data.created ?? data.inserted ?? 0} investors${data.updated ? ` · ${data.updated} updated` : ''}${data.skipped ? ` · ${data.skipped} skipped` : ''}${data.blocked ? ` · ${data.blocked} blocked` : ''}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const remove = async () => {
    if (!deleting) return;
    try {
      await axios.delete(`${API}/investors/${deleting.id}`, { withCredentials: true });
      setItems(prev => prev.filter(i => i.id !== deleting.id));
      toast.success('Investor removed (suppressed from future outreach)');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
    finally { setDeleting(null); }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-5" data-testid="investors-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Landmark className="w-6 h-6 text-emerald-600" /> Investors
            <Badge variant="outline" className="text-xs" data-testid="investors-count">{filtered.length}{filtered.length !== items.length ? ` / ${items.length}` : ''}</Badge>
          </h1>
          <p className="text-xs text-slate-500 mt-1">Fundraising partners for biotech clients. Add them to a biotech's Fundraising track from the company page.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={e => upload(e.target.files?.[0])} data-testid="investors-upload-input" />
          <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="investors-upload-btn">
            {uploading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1" />} Bulk upload
          </Button>
          <Button size="sm" variant="outline" asChild data-testid="investors-download-btn">
            <a href={`${API}/investors/download`} target="_blank" rel="noreferrer"><Download className="w-3.5 h-3.5 mr-1" /> Export</a>
          </Button>
          <Button size="sm" variant="outline" onClick={researchAll} disabled={bulkJob?.status === 'running'} data-testid="investors-research-all-btn">
            {bulkJob?.status === 'running' ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />} AI research all
          </Button>
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => { setEditing(null); setFormOpen(true); }} data-testid="add-investor-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add investor
          </Button>
        </div>
      </div>

      {bulkJob && bulkJob.status === 'running' && (
        <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-md px-3 py-2" data-testid="investors-research-progress">
          Researching {bulkJob.completed ?? 0} / {bulkJob.total ?? bulkJob.total_firms ?? '…'}{bulkJob.current_investor ? ` · ${bulkJob.current_investor}` : ''}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative max-w-sm flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Search firm, contact, TA, location…" value={search} onChange={e => setSearch(e.target.value)} className="pl-9 h-9 text-sm" data-testid="investors-search-input" />
        </div>
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} className="h-9 text-sm rounded-md border border-slate-200 bg-white px-2 max-w-[220px] truncate" data-testid="investors-type-filter">
          <option value="">All types</option>
          {types.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={stageFilter} onChange={e => setStageFilter(e.target.value)} className="h-9 text-sm rounded-md border border-slate-200 bg-white px-2" data-testid="investors-stage-filter">
          <option value="">All stages</option>
          {stages.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <Button size="sm" variant={highlightedOnly ? 'default' : 'outline'} className="h-9" onClick={() => setHighlightedOnly(v => !v)} data-testid="investors-highlighted-filter">
          <Star className={`w-3.5 h-3.5 mr-1 ${highlightedOnly ? 'fill-white' : ''}`} /> Highlighted
        </Button>
      </div>

      {loading ? (
        <div className="py-16 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
      ) : filtered.length === 0 ? (
        <Card className="border border-dashed border-slate-200"><CardContent className="py-12 text-center text-sm text-slate-400" data-testid="investors-empty">No investors match.</CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {filtered.map(inv => {
            const tas = listify(inv.preferred_therapeutic_areas).length ? listify(inv.preferred_therapeutic_areas) : listify(inv.investment_focus);
            const stagesL = listify(inv.investment_stage);
            return (
              <Card key={inv.id} className={`border shadow-sm hover:shadow-md transition-shadow ${inv.highlighted ? 'border-amber-300 bg-amber-50/30' : 'border-slate-200'}`} data-testid={`investor-card-${inv.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-900 truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>{inv.firm_name || inv.name}</p>
                      {inv.firm_name && inv.name && inv.name !== inv.firm_name && <p className="text-xs text-slate-500 truncate">{inv.name}{inv.designation ? ` · ${inv.designation}` : ''}</p>}
                    </div>
                    <button onClick={() => toggleHighlight(inv)} className="shrink-0" title={inv.highlighted ? 'Remove highlight' : 'Highlight'} data-testid={`investor-highlight-${inv.id}`}>
                      <Star className={`w-4 h-4 ${inv.highlighted ? 'fill-amber-400 text-amber-500' : 'text-slate-300 hover:text-amber-400'}`} />
                    </button>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {inv.investor_type && <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">{inv.investor_type}</Badge>}
                    {stagesL.slice(0, 3).map(s => <Badge key={s} variant="outline" className="text-[10px] bg-sky-50 text-sky-700 border-sky-200">{s}</Badge>)}
                    {stagesL.length > 3 && <Badge variant="outline" className="text-[10px]">+{stagesL.length - 3}</Badge>}
                  </div>
                  {tas.length > 0 && <p className="mt-2 text-[11px] text-slate-600 line-clamp-2"><span className="text-slate-400">TA:</span> {tas.join(', ')}</p>}
                  <div className="mt-2 space-y-0.5 text-[11px] text-slate-500">
                    {inv.location && <p className="flex items-center gap-1 truncate"><MapPin className="w-3 h-3 shrink-0" />{inv.location}</p>}
                    {inv.email && <p className={`flex items-center gap-1 truncate ${inv.email_status === 'bounced' ? 'text-rose-600' : ''}`}><Mail className="w-3 h-3 shrink-0" />{inv.email}{inv.email_status === 'bounced' ? ' (bounced)' : ''}</p>}
                    {inv.website && <a href={inv.website} target="_blank" rel="noreferrer" className="flex items-center gap-1 truncate hover:text-emerald-700"><Globe className="w-3 h-3 shrink-0" />{inv.website.replace(/^https?:\/\//, '')}</a>}
                  </div>
                  <div className="mt-3 flex items-center justify-between gap-1">
                    <Button size="sm" variant="ghost" className="h-7 text-[11px] text-violet-700 hover:bg-violet-50" onClick={() => research(inv)} disabled={!!researching[inv.id]} data-testid={`investor-research-${inv.id}`}>
                      {researching[inv.id] ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />} AI research
                    </Button>
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => { setEditing(inv); setFormOpen(true); }} data-testid={`investor-edit-${inv.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-rose-600 hover:bg-rose-50" onClick={() => setDeleting(inv)} data-testid={`investor-delete-${inv.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <InvestorFormDialog open={formOpen} onOpenChange={setFormOpen} investor={editing}
        onSaved={(saved) => { setItems(prev => editing ? prev.map(i => i.id === saved.id ? saved : i) : [saved, ...prev]); }} />

      <Dialog open={!!deleting} onOpenChange={(v) => { if (!v) setDeleting(null); }}>
        <DialogContent className="max-w-sm" data-testid="investor-delete-dialog">
          <DialogHeader><DialogTitle>Remove investor?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600"><span className="font-medium">{deleting?.firm_name || deleting?.name}</span> will be deleted and its email suppressed from future outreach.</p>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setDeleting(null)}>Cancel</Button>
            <Button size="sm" className="bg-rose-600 hover:bg-rose-700 text-white" onClick={remove} data-testid="investor-delete-confirm">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
